/**
 * O que o motor viu quando o alerta disparou, em linguagem de quem atende.
 *
 * Cada regra grava no evento o retrato que usou pra decidir (`verdict.value`
 * no `payload` do `fired`). A ocorrência mostrava só a frase — "Porta aberta
 * há 5 min" — e a pergunta seguinte de quem abre o alerta é sempre a mesma:
 * qual sensor, quanto mediu, contra que limite. Isso já estava no banco.
 *
 * Duas regras, as mesmas dos cards de curiosidade:
 *   - sem valor, não há linha. Nada de "—": linha vazia parece medição nula;
 *   - nome de sensor sai do payload (`device_name`, ou `name` nas listas),
 *     que é como TODAS as regras gravam — ver alerts/alerts/rules.py.
 *
 * Tipo sem leitura conhecida devolve vazio e a seção não aparece.
 */

import { formatDate } from "@/lib/format";

export interface LinhaDeDetalhe {
  rotulo: string;
  valor: string;
}

export interface DetalhesDoAlerta {
  /** Sensores envolvidos, sem repetição, na ordem em que a regra os listou. */
  sensores: string[];
  linhas: LinhaDeDetalhe[];
}

type Payload = Record<string, unknown>;

function numero(v: unknown, casas = 1): string | null {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString("pt-BR", { maximumFractionDigits: casas });
}

function texto(v: unknown): string | null {
  return typeof v === "string" && v.trim() !== "" ? v.trim() : null;
}

function lista(p: Payload, chave: string): Payload[] {
  const v = p[chave];
  return Array.isArray(v) ? (v.filter((x) => x && typeof x === "object") as Payload[]) : [];
}

function faixa(min: unknown, max: unknown, unidade: string): string | null {
  const a = numero(min);
  const b = numero(max);
  if (a && b) return `${a} a ${b} ${unidade}`;
  if (a) return `mínimo ${a} ${unidade}`;
  if (b) return `máximo ${b} ${unidade}`;
  return null;
}

const LISTAS_DE_SENSORES = ["sensores", "offline", "low", "weak", "off", "congelados"];

function sensoresDoPayload(p: Payload): string[] {
  const nomes: string[] = [];
  const add = (n: string | null) => {
    if (n && !nomes.includes(n)) nomes.push(n);
  };
  add(texto(p.device_name));
  for (const chave of LISTAS_DE_SENSORES) {
    for (const item of lista(p, chave)) add(texto(item.device_name) ?? texto(item.name));
  }
  return nomes;
}

export function detalhesDoAlerta(
  kind: string | null | undefined,
  payload: Payload | null | undefined,
): DetalhesDoAlerta {
  const p: Payload = payload ?? {};
  const linhas: LinhaDeDetalhe[] = [];
  const add = (rotulo: string, valor: string | null) => {
    if (valor) linhas.push({ rotulo, valor });
  };
  const comUnidade = (v: unknown, unidade: string, casas = 1) => {
    const n = numero(v, casas);
    return n ? `${n} ${unidade}` : null;
  };

  switch (kind) {
    case "temp_out_of_range": {
      add("Faixa configurada", faixa(p.setpoint_min, p.setpoint_max, "°C"));
      const sensores = lista(p, "sensores");
      if (sensores.length > 0) {
        for (const s of sensores) {
          const t = comUnidade(s.temperature, "°C");
          const fora = typeof s.desvio === "number" && s.desvio > 0;
          add(texto(s.device_name) ?? texto(s.role) ?? "Sensor", t && (fora ? `${t} · fora da faixa` : t));
        }
        const total = sensores.length;
        if (total > 1) add("Sensores fora da faixa", `${numero(p.fora_da_faixa, 0) ?? 0} de ${total}`);
      } else {
        add("Medido", comUnidade(p.temperature, "°C"));
      }
      break;
    }
    case "door_open_too_long":
      add("Aberta desde", texto(p.opened_at) && formatDate(p.opened_at as string, "dd/MM HH:mm"));
      add("Tempo aberta", comUnidade(p.minutes_open, "min", 0));
      add("Limite", comUnidade(p.limit, "min", 0));
      break;
    case "power_off":
      add("Maior potência na janela", comUnidade(p.max_watts_in_window, "W"));
      add("Considerado desligado abaixo de", comUnidade(p.threshold, "W"));
      add("Janela avaliada", comUnidade(p.minutes_min, "min", 0));
      add("Leitura mais recente", numero(p.idade_leitura_min) && `há ${numero(p.idade_leitura_min)} min`);
      break;
    case "power_overload":
      add("Potência média", comUnidade(p.avg_watts, "W"));
      add("Pico", comUnidade(p.max_watts, "W"));
      add("Potência nominal", comUnidade(p.nominal_power, "W"));
      add("Acima do nominal", comUnidade(p.overload_pct, "%"));
      add("Sustentado por", comUnidade(p.sustained_min, "min", 0));
      break;
    case "current_spike":
      add("Corrente medida", comUnidade(p.current_a, "A"));
      add("Corrente nominal", comUnidade(p.nominal_a, "A"));
      add("Acima do nominal", comUnidade(p.spike_pct, "%"));
      add("Fase", texto(p.phase));
      break;
    case "low_battery":
      for (const s of lista(p, "low")) add(texto(s.name) ?? "Sensor", comUnidade(s.battery_pct, "%", 0));
      add("Aviso abaixo de", comUnidade(p.warn_at, "%", 0));
      break;
    case "weak_signal":
      for (const s of lista(p, "weak")) {
        const v = numero(s.value, 0);
        add(texto(s.name) ?? "Sensor", v && `${v} ${texto(s.unit) ?? ""}`.trim());
      }
      break;
    case "device_offline":
      for (const s of lista(p, "offline")) {
        const idade = numero(s.idade_min, 0);
        add(texto(s.name) ?? "Sensor", idade ? `sem leitura há ${idade} min` : texto(s.reason));
      }
      break;
    case "plug_off":
      for (const s of lista(p, "off")) add(texto(s.name) ?? "Tomada", "relé desligado");
      break;
    case "medicao_incoerente":
      for (const s of lista(p, "congelados")) add(texto(s.name) ?? "Medidor", "leitura congelada");
      break;
  }

  return { sensores: sensoresDoPayload(p), linhas };
}
